import { TFunction } from 'i18next';
import { ObjectSchema } from 'yup';
import { dateSeperator } from '../../../../config';
import {
  ContractTypeEnum,
  contractTypeOptions,
  genderOptions,
  housingSituationOptions,
  maritalStatusOptions,
  professionOptions,
  telephoneCountryCodesOptions,
} from '../../../../resources/base/models';
import { CountryCodesEnum, countryCodesOptionsValues } from '../../../../resources/base/models/country';
import { EnumCountryCode } from '../../../../resources/base/models/enums';
import { dateFormat } from '../../../../services/common/format';
import { getOptionsListValues } from '../../../../services/common/miscUtils';
import { phoneValidationUtils, Yup } from '../../../../services/common/validation';
import { validateEmail, validateMobilePhone } from '../../../../services/common/validationUtils';
import { getOccupationGroupFlags } from '../occupationBasedFieldsFilter';
import { generateCompanyDataFormValidationSchema } from '../utils';
import { AddressDataFormType, CompanyDataFormType, ContactDataFormType, FinancialDataFormType } from './initialValues';

const regexNumeric = /^[0-9]+$/;
const regexName =
  /^[a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß'\\.-][a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß '\\.-]+$/;
const regexNumbersAndLetters =
  /^[-a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß\d\\.'][-a-zA-ZÄÅáâäàãåÉéêëèíîïìÖóôöòõÜúüûùÇçÑñŠšß \\.0-9']*$/;

let _t = (() => '') as TFunction;
// eslint-disable-next-line prettier/prettier
const t =
  (...args: Parameters<TFunction>) =>
  () =>
    _t(...args);

const gender = Yup.string()
  .required(t('validation:requiredSelect'))
  .oneOf(getOptionsListValues(genderOptions), t('validation:required'));
const firstName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .matches(regexName, t('validation:invalidCharacter'))
  .required(t('validation:required'));
const lastName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .matches(regexName, t('validation:invalidCharacter'))
  .required(t('validation:required'));
const birthName = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .matches(regexName, t('validation:invalidCharacter'))
  .required(t('validation:required'));
const email = validateEmail();
const mobilePhone = validateMobilePhone();
const telephonePhone = Yup.string()
  .matches(phoneValidationUtils.landline.regexWithPrefix, t('validation:invalidCharacter'))
  .transform((value) => value.replace(/\s+/g, ''))
  .landlinePhoneLength();
const dateOfBirth = Yup.string()
  .required(t('validation:requiredSelect'))
  .dateFormat(dateFormat, dateSeperator, t('validation:invalidDateformat'))
  .notInFuture(dateSeperator, t('validation:dateInFuture'))
  .fullAge(18, dateSeperator, t('validation:fullAge'));

const profession = Yup.string().oneOf(getOptionsListValues(professionOptions), t('validation:required'));

const mobilePhoneCountryCode = Yup.string()
  .oneOf(getOptionsListValues(telephoneCountryCodesOptions), t('validation:invalidData'))
  .required(t('validation:required'));

const birthCountry = Yup.string()
  .oneOf(countryCodesOptionsValues, t('validation:invalidData'))
  .required(t('validation:required'));

const zipCodeOfBirth = Yup.string()
  .min(5, t('validation:minLength', { value: 5 }))
  .max(5, t('validation:maxLength', { value: 5 }))
  .matches(regexNumeric, t('validation:invalidCharacter'))
  .when('birthCountry', {
    is: (value) => value === 'FR' || value === EnumCountryCode.FRANCE,
    then: (schema) => schema.required(t('validation:required')),
  });

const placeOfBirth = Yup.string()
  .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim())
  .when('birthCountry', {
    is: (value) => value === 'FR' || value === EnumCountryCode.FRANCE,
    then: (schema) => schema.required(t('validation:required')),
  });

const nationality = Yup.string()
  .oneOf(countryCodesOptionsValues, t('validation:invalidData'))
  .required(t('validation:required'));

const residenceStatus = Yup.string()
  .oneOf(getOptionsListValues(housingSituationOptions), t('validation:invalidData'))
  .required(t('validation:required'));

const addressLine1 = Yup.string()
  .required(t('validation:required'))
  .matches(regexNumbersAndLetters, t('validation:invalidCharacter'))
  .max(120, t('validation:maxLength', { value: 120 }));

const optionalLine = Yup.string()
  .matches(regexNumbersAndLetters, t('validation:invalidCharacter'))
  .max(120, t('validation:maxLength', { value: 120 }));

const zipCode = Yup.string()
  .matches(regexNumeric, t('validation:invalidCharacter'))
  .required(t('validation:required'))
  .length(5, t('validation:minLength', { value: 5 }));

const city = Yup.string()
  .required(t('validation:required'))
  .matches(regexName, t('validation:invalidCharacter'))
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }));

const countryCode = Yup.string().oneOf([CountryCodesEnum.FR]).required(t('validation:required'));

const maritalStatus = Yup.string()
  .oneOf(getOptionsListValues(maritalStatusOptions), t('validation:invalidData'))
  .required(t('validation:required'));

const numberOfDependentChildren = Yup.number().required(t('validation:required'));

const contractType = Yup.string()
  .oneOf(getOptionsListValues(contractTypeOptions), t('validation:invalidData'))
  .required(t('validation:required'));

const employmentStartDate = Yup.string()
  .required(t('validation:requiredSelect'))
  .dateFormat(dateFormat, dateSeperator, t('validation:required'))
  .notInFuture(dateSeperator, t('validation:dateInFuture'));

const employer = Yup.string()
  .required(t('validation:required'))
  .matches(regexName, t('validation:invalidCharacter'))
  .min(2, t('validation:minLength', { value: 2 }))
  .max(120, t('validation:maxLength', { value: 120 }))
  .when('contractType', {
    is: ContractTypeEnum.NO_CONTRACT,
    then: (schema) => schema.notRequired(),
  });

const countryCodeEmployer = Yup.string()
  .oneOf(countryCodesOptionsValues, t('validation:invalidData'))
  .required(t('validation:required'))
  .when('contractType', {
    is: ContractTypeEnum.NO_CONTRACT,
    then: (schema) => schema.notRequired(),
  });

const zipCodeEmployer = Yup.string()
  .length(5, t('validation:minLength', { value: 5 }))
  .required(t('validation:required'))
  .when('contractType', {
    is: ContractTypeEnum.NO_CONTRACT,
    then: (schema) => schema.notRequired(),
  });

const cityEmployer = Yup.string().when('contractType', {
  is: ContractTypeEnum.NO_CONTRACT,
  then: (schema) => schema.notRequired(),
  otherwise: (schema) =>
    schema
      .required(t('validation:required'))
      .test('no-edge-spaces', t('validation:spaceIssue'), (value) => !!value && value === value.trim()),
});

const netIncomeAmount = Yup.number().required(t('validation:required'));
const additionalIncomeAmount = Yup.number().required(t('validation:required'));
const monthlyRent = Yup.number().required(t('validation:required'));
const otherMonthlyCosts = Yup.number().required(t('validation:required'));
const _employmentCategory = Yup.string().oneOf(['MAIN_EMPLOYMENT']).required();

export const getContactDataFormValidationSchema = (__t: TFunction): ObjectSchema<ContactDataFormType> => {
  _t = __t;

  return Yup.object({
    gender,
    firstName,
    lastName,
    birthName,
    dateOfBirth,
    profession,
    email,
    mobilePhoneCountryCode,
    mobilePhone,
    telephonePhone,
    birthCountry,
    zipCodeOfBirth,
    placeOfBirth,
    nationality,
    residenceStatus,
    _employmentCategory,
  });
};

export const getAddressDataFormValidationSchema = (__t: TFunction): ObjectSchema<AddressDataFormType> => {
  _t = __t;

  return Yup.object({
    addressLine1,
    optionalLine,
    zipCode,
    city,
    countryCode,
  });
};

export const getFinancialDataFormValidationSchema = (
  mainPosFrOccupation: CreditIntegration.Schemas.OccupationEnumValue,
  __t: TFunction
): ObjectSchema<FinancialDataFormType> => {
  _t = __t;

  const { isOccupationGroupA, isOccupationGroupB } = getOccupationGroupFlags(mainPosFrOccupation);

  return Yup.object({
    applicationFor: Yup.mixed(),
    maritalStatus,
    numberOfDependentChildren,
    contractType: isOccupationGroupB ? Yup.mixed() : contractType,
    employmentStartDate: isOccupationGroupB ? Yup.mixed() : employmentStartDate,
    employer: isOccupationGroupA ? Yup.mixed() : employer,
    countryCodeEmployer: isOccupationGroupA ? Yup.mixed() : countryCodeEmployer,
    zipCodeEmployer: isOccupationGroupA ? Yup.mixed() : zipCodeEmployer,
    cityEmployer: isOccupationGroupA ? Yup.mixed() : cityEmployer,
    netIncomeAmount,
    additionalIncomeAmount,
    monthlyRent,
    otherMonthlyCosts,
  });
};

export const getCompanyDataFormValidationSchema = (__t: TFunction): Yup.Lazy<ObjectSchema<CompanyDataFormType>> => {
  _t = __t;

  return Yup.lazy((values: CompanyDataFormType) => {
    return generateCompanyDataFormValidationSchema(_t, values) as any;
  });
};
